import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "../api";
import Shell from "../Shell";
import { authHeaders } from "../api";
import "../dashboard.css";

/* ───────────────────────────── Small helpers ───────────────────────────── */

function useDebounced(v, ms = 300) {
  const [s, setS] = useState(v);
  useEffect(() => {
    const t = setTimeout(() => setS(v), ms);
    return () => clearTimeout(t);
  }, [v, ms]);
  return s;
}

const todayIso = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

const isoToDate = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const dateToIso = (date) => {
  if (!(date instanceof Date)) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const fmtDMY = (iso) => {
  const dt = isoToDate(iso);
  if (!dt) return "";
  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const yyyy = dt.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

/* ─────────────── Tiny inline calendar (DD/MM view, ISO value) ─────────────── */

function MiniDatePickerDMY({ value, onChange, maxDate }) {
  const [open, setOpen] = useState(false);
  const selected = isoToDate(value) || null;
  const initialView = selected || new Date();
  const [view, setView] = useState(new Date(initialView.getFullYear(), initialView.getMonth(), 1));
  const wrapRef = useRef(null);

  useEffect(() => {
    const h = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    const s = isoToDate(value);
    if (s) setView(new Date(s.getFullYear(), s.getMonth(), 1));
  }, [value]);

  const startOfMonth = new Date(view.getFullYear(), view.getMonth(), 1);
  const startDay = (startOfMonth.getDay() + 6) % 7;
  const daysInMonth = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();

  const prevMonth = () => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1));
  const nextMonth = () => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1));

  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(view.getFullYear(), view.getMonth(), d));
  while (cells.length % 7 !== 0) cells.push(null);
  while (cells.length < 42) cells.push(null);

  const isSameDay = (a, b) =>
    a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  const isAfterMax = (c) => {
    if (!maxDate || !c) return false;
    const cellDate = new Date(c.getFullYear(), c.getMonth(), c.getDate()).getTime();
    const max = isoToDate(maxDate);
    if (!max) return false;
    const maxOnly = new Date(max.getFullYear(), max.getMonth(), max.getDate()).getTime();
    return cellDate > maxOnly;
  };

  return (
    <div ref={wrapRef} className="inc-date-wrap">
      <input
        readOnly
        value={fmtDMY(value)}
        onClick={() => setOpen((v) => !v)}
        className="inc-input"
        placeholder="dd/mm/áááá"
        aria-label="Veldu dagsetningu"
      />
      <input type="hidden" value={value || ""} />

      {open && (
        <div className="inc-date-popover">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 8,
              gap: 8,
            }}
          >
            <button className="btn ghost sm" type="button" onClick={prevMonth}>◀</button>
            <div style={{ fontWeight: 600 }}>
              {view.toLocaleString("is-IS", { month: "long" })} {view.getFullYear()}
            </div>
            <button className="btn ghost sm" type="button" onClick={nextMonth}>▶</button>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 32px)",
              gap: 4,
              textAlign: "center",
              opacity: 0.7,
              fontSize: 12,
              marginBottom: 4,
            }}
          >
            {["Mán", "Þri", "Mið", "Fim", "Fös", "Lau", "Sun"].map((d) => <div key={d}>{d}</div>)}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 32px)",
              gap: 4,
              textAlign: "center",
            }}
          >
            {cells.map((c, i) => {
              const active = c && selected && isSameDay(c, selected);
              const disabled = !c || isAfterMax(c);

              return (
                <button
                  key={i}
                  type="button"
                  disabled={disabled}
                  onClick={() => {
                    if (!c || disabled) return;
                    onChange?.(dateToIso(c));
                    setOpen(false);
                  }}
                  style={{
                    width: 32,
                    height: 28,
                    borderRadius: 6,
                    border: "1px solid #d7dbe3",
                    opacity: disabled ? 0.35 : 1,
                    background: active ? "#e0ecff" : "#fff",
                    cursor: disabled ? "default" : "pointer",
                  }}
                >
                  {c ? c.getDate() : ""}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ───────────────────────────── Layout helper ───────────────────────────── */

function Row({ label, children }) {
  return (
    <div className="inc-row">
      <div className="inc-label">{label}</div>
      <div className="inc-field">{children}</div>
    </div>
  );
}

/* ───────────────────────────── Page ───────────────────────────── */

export default function NewIncident() {
  const qs = new URLSearchParams(useLocation().search);
  const preselectUserId = qs.get("user");
  const memberInputRef = useRef(null);

  const [isAdmin, setIsAdmin] = useState(false);
  const [myName, setMyName] = useState("");

  const [userId, setUserId] = useState(preselectUserId || "");
  const [userQuery, setUserQuery] = useState("");
  const dUserQuery = useDebounced(userQuery, 300);
  const [userOptions, setUserOptions] = useState([]);

  const [creatorId, setCreatorId] = useState(null);
  const [staffOptions, setStaffOptions] = useState([]);

  const [incidentDate, setIncidentDate] = useState(todayIso());
  const [timeTaken, setTimeTaken] = useState("");
  const [inHouse, setInHouse] = useState(false);
  const [onPhone, setOnPhone] = useState(false);
  const [email, setEmail] = useState(false);
  const [description, setDescription] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/api/user/role/", { headers: authHeaders() });
        setIsAdmin(data.role === "admin");

        if (data.user_id) {
          const r = await api.get(`/api/users/${data.user_id}/summary/`, {
            headers: authHeaders(),
          });
          const name = r.data.full_name || r.data.username || "—";
          setMyName(name);
          setCreatorId(data.user_id);
        }
      } catch {}
    })();
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const { data } = await api.get("/api/users-list/", {
          params: { role: "staff", order_by: "name" },
          headers: authHeaders(),
        });
        setStaffOptions(Array.isArray(data) ? data : []);
      } catch {}
    })();
  }, [isAdmin]);

  useEffect(() => {
    if (!dUserQuery && !preselectUserId) {
      setUserOptions([]);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/api/users-list/", {
          params: dUserQuery ? { q: dUserQuery, order_by: "name" } : {},
          headers: authHeaders(),
        });
        setUserOptions(Array.isArray(data) ? data : []);
      } catch {}
    })();
  }, [dUserQuery, preselectUserId]);

  const preselectedUser = useMemo(
    () => userOptions.find((u) => String(u.id) === String(userId)),
    [userOptions, userId]
  );

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!userId) return alert("Veldu félagsaðila.");
    if (!description.trim()) return alert("Vantar lýsingu atviks.");

    const body = {
      user: Number(userId),
      incident_date: incidentDate,
      time_taken: timeTaken || "",
      in_house: inHouse,
      on_phone: onPhone,
      email,
      description: description.trim(),
      ...(isAdmin && creatorId ? { created_by: Number(creatorId) } : {}),
    };

    try {
      await api.post("/api/incidents/", body, { headers: authHeaders() });
      alert("Atvik skráð.");

      setUserId("");
      setUserQuery("");
      setUserOptions([]);
      setIncidentDate(todayIso());
      setTimeTaken("");
      setInHouse(false);
      setOnPhone(false);
      setEmail(false);
      setDescription("");

      setTimeout(() => memberInputRef.current?.focus(), 0);
    } catch (e2) {
      alert(
        e2?.response?.data?.detail ||
        e2?.response?.data?.non_field_errors?.[0] ||
        "Tókst ekki að skrá atvik."
      );
    }
  };

  return (
    <Shell title="Hugarafl starfsfólk" rightMenu>
      <h2 style={{ textAlign: "center", margin: "8px 0 18px" }}>Skrá atvik</h2>

      <form onSubmit={onSubmit} className="placeholder inc-form" style={{ maxWidth: 760 }}>
        <Row label="Félagsaðili:">
          <div className="inc-relative">
            <input
              ref={memberInputRef}
              className="inc-input"
              placeholder="Leita að nafni / notendanafni / netfangi / síma"
              value={preselectedUser ? preselectedUser.full_name : userQuery}
              onChange={(e) => {
                setUserQuery(e.target.value);
                setUserId("");
              }}
              onFocus={() => {
                if (!userQuery && preselectedUser) setUserQuery(preselectedUser.full_name);
              }}
            />

            {userOptions.length > 0 && !userId && (
              <div className="inc-typeahead">
                {userOptions.map((u) => (
                  <div
                    key={u.id}
                    onMouseDown={() => {
                      setUserId(u.id);
                      setUserQuery(u.full_name);
                    }}
                    className="inc-typeahead-item"
                  >
                    {u.full_name} <span style={{ opacity: 0.6 }}>({u.username})</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Row>

        <Row label="Skráð af:">
          {isAdmin ? (
            <select
              className="inc-input"
              value={creatorId || ""}
              onChange={(e) => setCreatorId(e.target.value || null)}
            >
              {staffOptions.length === 0 && <option value="">Hleð…</option>}
              {staffOptions.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.full_name}
                </option>
              ))}
            </select>
          ) : (
            <div style={{ opacity: 0.85, wordBreak: "break-word" }}>{myName || "—"}</div>
          )}
        </Row>

        <Row label="Dagsetning atviks:">
          <MiniDatePickerDMY value={incidentDate} onChange={setIncidentDate} maxDate={todayIso()} />
        </Row>

        <Row label="Tímalengd:">
          <input
            type="text"
            value={timeTaken}
            onChange={(e) => setTimeTaken(e.target.value)}
            placeholder="t.d. 45 mín eða 1 klst"
            className="inc-input inc-short-input"
          />
        </Row>

        <Row label="Staðsetning / aðferð:">
          <div className="inc-flags">
            <label className="inc-check">
              <input type="checkbox" checked={inHouse} onChange={(e) => setInHouse(e.target.checked)} />
              Í hús
            </label>

            <label className="inc-check">
              <input type="checkbox" checked={onPhone} onChange={(e) => setOnPhone(e.target.checked)} />
              Sími
            </label>

            <label className="inc-check">
              <input type="checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} />
              Email
            </label>
          </div>
        </Row>

        <Row label="Lýsing:">
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Lýstu atvikinu stuttlega…"
            rows={5}
            className="inc-input"
            style={{ resize: "vertical" }}
          />
        </Row>

        <div style={{ textAlign: "center", marginTop: 16 }}>
          <button className="btn" type="submit" disabled={!userId || !description.trim()}>
            Skrá atvik
          </button>
        </div>
      </form>
    </Shell>
  );
}